import { deleteField, writeBatch } from 'firebase/firestore'
import { useCallback, useContext, useMemo } from 'react'
import { ParentContext } from '../ParentContext';
import { createRefPath_event } from 'global/firestoreData/appData/appRefPaths';
import { logBatch } from 'global/cnr/reducers/reducerHelpers/logging';
import _ from 'lodash';
import { _cacheGroupTypes } from './useTimestamp';
import { _validationIssueTypes } from './useCleanData';

export const _allowIssuesUpdate = true
const _allowDuplicateUpdate = false

export const useDuplicates = ({ appDataDocuments, appUserCollection }) => {

  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { app_state, paps_state } = states
  const { mainDb } = app_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { database_fns } = fns

  const getAppUserDuplicates = () => {
    const _dups = {}
    const _appUserDocument = appDataDocuments?.[appUserCollection]
    if (_appUserDocument) {
      const _grp = _.groupBy(_appUserDocument, (item) =>
        `${item.firstName}_${item.lastName}`
      );
      _.forEach(_grp, (items) => {
        if (_.size(items) > 1) {
          const firstItem = items[0]
          if (!_dups[`${firstItem.firstName}_${firstItem.lastName}`]) { _dups[`${firstItem.firstName}_${firstItem.lastName}`] = [] }
          _.forEach(items, i => {
            _dups[`${firstItem.firstName}_${firstItem.lastName}`].push(i)
          })
        }
      })
    }

    return _dups
  }

  const mergeDuplicates = () => {
    const _appUserDocument = appDataDocuments?.[appUserCollection];
    if (!_appUserDocument) return appDataDocuments;

    const _grp = _.groupBy(_appUserDocument, (item) =>
      `${item.firstName}_${item.lastName}`
    );

    _.forEach(_grp, (items) => {
      if (_.size(items) > 1) {
        const primary = items[0];

        const merged = _.reduce(
          items,
          (acc, item) => {
            return _.mergeWith(acc, item, (objValue, srcValue, key) => {
              // 🚫 NEVER merge _itemStatusType
              if (key === '_itemStatusType') {
                return objValue;
              }

              // Arrays → union
              if (_.isArray(objValue)) {
                return _.union(objValue, srcValue);
              }

              // Fill empty values
              if (_.isNil(objValue) || objValue === '') {
                return srcValue;
              }

              return objValue;
            });
          },
          _.cloneDeep(primary) // 🧠 avoid mutating during merge
        );

        // Apply merged result back to primary
        _.assign(primary, merged);

        // 🔥 Always remove _itemStatusType from primary
        delete primary._itemStatusType;

        // Ensure primary stays active
        // primary._itemStatusType = primary._itemStatusType || 'active';

        // Mark ONLY the others inactive
        _.forEach(items.slice(1), (item) => {
          if (item !== primary) {
            item._itemStatusType = 'inactive';
          }
        });
      }
    });

    return getAppUserDuplicates(appDataDocuments, appUserCollection)
  };

  const updateDuplicates = useCallback((duplicates) => {
    const batch = writeBatch(mainDb);
    const docsToUpdate = {};
    _.forEach(duplicates, dupItem => {
      _.forEach(dupItem, dup => {
        const { _itemKey } = dup
        const refPath = createRefPath_event(pathViews, [appUserCollection, _itemKey]);
        database_fns.set_doc(refPath, dup, true, false, batch)
        docsToUpdate[_itemKey] = dup;
      })
    })
    const refPath_docs = createRefPath_event(pathViews, ['_documents', appUserCollection]);
    //refPath, data, merge, dispatch, batch, altDb
    database_fns.set_doc(refPath_docs, docsToUpdate, true, null, batch);
    logBatch(batch)
    if (_allowDuplicateUpdate) {
      batch.commit()
    }
  }, []);

  const updateDataUpdates = useCallback(({ vds, selectedItems }) => {
    const { dataUpdates, appDoc, _dataUpdates } = vds
    // console.log('vds', vds)
    // return;
    console.clear()
    console.group('updateDataUpdates')
    console.log('selectedItems', selectedItems)
    console.log('_dataUpdates', _dataUpdates)

    const dataUpdateItemz = groupDotNotationUpdatez(_.cloneDeep(_dataUpdates), selectedItems)

    console.log('appDoc', appDoc)
    console.log('dataUpdateItemz', dataUpdateItemz)

    // init the batch
    const batch = writeBatch(mainDb);

    // create the ref for _documents
    const refPath_docs = createRefPath_event(pathViews, ['_documents', appDoc]);
    console.group('_documents UPDATE', _.size(dataUpdateItemz._documents))
    console.log('refPath_docs', refPath_docs)
    console.log('_documents', dataUpdateItemz._documents)
    console.groupEnd()

    // update doc using batch
    database_fns.update_doc(refPath_docs, dataUpdateItemz._documents, null, batch)

    // loop the dataUpdateItems
    _.forEach(dataUpdateItemz.documents, (dui, duiKey) => {
      // create the ref for appDoc + key
      const refPath_doc = createRefPath_event(pathViews, [appDoc, duiKey]);
      console.group(dui === 'forDelete' ? 'document DELETE' : 'document UPDATE')
      console.log('refPath_doc', refPath_doc)
      console.log('doc', dui)
      console.groupEnd()
      if (dui === 'forDelete') {
        // refPath, dispatch, batch 
        database_fns.delete_doc(refPath_doc, null, batch)
      } else {
        // update doc using batch
        database_fns.update_doc(refPath_doc, dui, null, batch)
      }
    })

    // logBatch(batch)
    if (_allowIssuesUpdate) {
      const _timestamp = Date.now()
      database_fns.update_timestamp(_cacheGroupTypes.eventInfo, pathViews.events, _timestamp, batch)
      batch.commit()
    }

    console.groupEnd()

  }, []);

  // krizpz2@yahoo.com

  const duplicates = useMemo(() => {
    return mergeDuplicates()
  }, [appDataDocuments, appUserCollection])

  return { duplicates, updateDuplicates, updateDataUpdates }

}

const groupDotNotationUpdates = (flatUpdates) => {
  console.log('flatUpdates', flatUpdates)
  return _.reduce(
    flatUpdates,
    (acc, value, flatKey) => {
      const parts = flatKey.split('.')

      const docId = parts[0]
      const fieldPath = parts.slice(1)

      if (!docId || fieldPath.length === 0) return acc

      const fieldKey = fieldPath.join('.')

      if (!acc[docId]) acc[docId] = {}

      // set nested path inside the doc object
      _.set(acc[docId], fieldKey, value)

      return acc
    },
    {}
  )
}

const groupDotNotationUpdatez = (dataUpdates, selectedItems) => {
  const _updates = {
    documents: {},
    _documents: {}
  }

  _.forEach(dataUpdates, (dataUpdate, dataUpdateKey) => {
    _.forEach(dataUpdate, (dataUpdateItem, dataUpdateItemKey) => {
      const { path, updateType, updateDataValue, itemProp, dups, rfz } = dataUpdateItem
      switch (updateType) {
        case _validationIssueTypes.emptyObject:
        case _validationIssueTypes.emptyString:
        case _validationIssueTypes.emptyArray:
          if (!_updates.documents[dataUpdateKey]) { _updates.documents[dataUpdateKey] = {} }
          _updates._documents[path] = deleteField()
          _updates.documents[dataUpdateKey][itemProp] = deleteField()
          break;

        case _validationIssueTypes.duplicateArrayItems:
          if (!_updates.documents[dataUpdateKey]) { _updates.documents[dataUpdateKey] = {} }
          _updates._documents[path] = updateDataValue
          _updates.documents[dataUpdateKey][itemProp] = updateDataValue
          break;

        case _validationIssueTypes.duplicateItems:
          if (dups) {
            _.forEach(dups, (dup, dupKey) => {
              const { duplicateItems } = dup
              const diKeys = _.mapValues(duplicateItems, (di) => di._itemKey)
              _.forEach(diKeys, diKey => {
                if (!_.isEmpty(selectedItems)) {
                  if (selectedItems[diKey]) {
                    _updates._documents[diKey] = deleteField()
                    _updates.documents[diKey] = 'forDelete'
                  }
                } else {
                  _updates._documents[diKey] = deleteField()
                  _updates.documents[diKey] = 'forDelete'
                }
              })
            })
          }
          break;
        case _validationIssueTypes.missingArrayKeys:
          if (rfz) {
            _.forEach(rfz, (rfzi, dupKey) => {
              const { originalItem, arrayKey, missingKey, _itemKey } = rfzi
              const currentVal = originalItem?.[arrayKey]
              const newVal = _.without(currentVal, missingKey);
              if (!_updates.documents[_itemKey]) { _updates.documents[_itemKey] = {} }
              const _path = `${_itemKey}.${arrayKey}`
              _updates._documents[_path] = newVal
              _updates.documents[_itemKey][arrayKey] = newVal
            })
          }
          break;
        default:
        // nothing
      }

    })
  })
  return _updates
}

